import { createSelector } from '@ngrx/store';
import * as RA from 'ramda-adjunct';
import { getManagementState, ManagementState } from '../management.reducer';
import { adapter, ItemsState } from './items.reducer';
import {asyncActionState} from '@emm/core/util/async-action-state';
import {Item} from '@emm/core/models/item.model';
import {SearchFilter} from '@emm/core/models/search-filter';
import {ItemAttributeSummary} from '@emm/core/models/item-attribute.model';

const getState = createSelector(getManagementState, (state: ManagementState) => state.items);

const entitySelectors = adapter.getSelectors(getState);
const asyncActionStateSelectors = asyncActionState.getSelectors(getState);

const getInitSize = createSelector(getState, (state: ItemsState) => state.initSize);
const getSelectedTag = createSelector(getState, (state: ItemsState) => state.selectedTag);
const getSelected = createSelector(
    entitySelectors.selectEntities,
    getSelectedTag,
    (entities, selectedTag) => selectedTag && entities[selectedTag]
);
const getValidAll = createSelector(
    entitySelectors.selectAll,
    asyncActionStateSelectors.getHasError,
    asyncActionStateSelectors.getSuccess,
    (items, hasError, success) => (!hasError && success ? items : null)
);
const getSearchFilter = createSelector(getState, (state: ItemsState) => state.searchFilter);
const getFilteredItems = createSelector(
    getValidAll,
    getSearchFilter,
    (items: Item[], filter: SearchFilter) => {
        const useFilter =
            RA.isNotNilOrEmpty(filter) && RA.isNonEmptyString(filter.key) && RA.isNotNil(filter.value);

        const filterString = Array.isArray(filter?.value)
            ? (filter?.value as string[]).join(',').toLowerCase()
            : filter?.value
                ? (filter?.value.toLowerCase() as string)
                : '';

        const filterMap = (item: Item) => {
            switch (filter.key) {
                case 'tag': {
                    const stripped = item.tag.replace(/[_-]/g, ''); // remove underscores
                    return (
                        item.tag.toLowerCase().includes(filterString) ||
                        stripped.toLowerCase().includes(filterString)
                    );
                }
                case 'itemTypeName': {
                    return item.itemTypeName.toLowerCase().includes(filterString);
                }
                default: {
                    return (
                        (item.attributes as ItemAttributeSummary[]).filter((attribute) => {
                            const value = attribute.value
                                ? attribute.value
                                    .toLowerCase()
                                    .replace(/<sup>.<\/sup>/g, '')
                                    .replace(/[®™©]/g, '')
                                : '';

                            return (
                                attribute.attribute === filter.key &&
                                attribute.value &&
                                value.includes(filterString)
                            );
                        }).length > 0
                    );
                }
            }
        };

        return RA.isNotNilOrEmpty(items) && useFilter ? items.filter(filterMap) : items;
    }
);

export const ItemsSelectors = {
    entitySelectors,
    ...asyncActionStateSelectors,
    getInitSize,
    getSelectedTag,
    getSelected,
    getValidAll,
    getSearchFilter,
    getFilteredItems
};
